import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useGameContextContext } from "./context";

/**
 * React hook providing access to the persisted game clock.
 *
 * The clock state lives in core (gameContext.gameClock, persisted in
 * IndexedDB, mirrored to Redux via updateGameContext). This hook just
 * provides:
 *  - displayTime: the live computed time, read fresh from the clock on
 *    every render.
 *  - isRunning: whether the clock is ticking.
 *  - toggleClock: pause/resume via core (persists).
 *  - getCurrentTime: same as displayTime, callable.
 *  - runningClockEnabled: whether the game uses the running clock
 *    (gameContext.clockType === "running", persisted by core).
 *
 * Pass { tick: true } when the consumer needs the display to refresh on
 * every animation frame (e.g., the main clock view). Other consumers
 * (e.g., fragment time selectors) read on-demand without RAF cost.
 */
const useRunningClock = ({ tick: shouldTick = false } = {}) => {
	const {
		state: gameContextState,
		pauseGameClock,
		resumeGameClock
	} = useGameContextContext();
	const runningClockEnabled = gameContextState.runningClockEnabled();
	const gameClock = gameContextState.gameClock();
	const gameId = useSelector(({ gameContext }) => gameContext.gameId);

	// Counter used to force re-renders from the tick interval. The actual
	// clock value comes from gameClock.now() on every render.
	// eslint-disable-next-line no-unused-vars
	const [, setRenderTick] = useState(0);
	const intervalRef = useRef(null);
	const lastSecRef = useRef(null);

	const isRunning =
		gameClock && typeof gameClock.isPaused === "function"
			? !gameClock.isPaused()
			: false;

	useEffect(() => {
		if (shouldTick && runningClockEnabled && isRunning) {
			lastSecRef.current = null;
			intervalRef.current = setInterval(() => {
				const nowMs =
					gameClock && typeof gameClock.now === "function" ? gameClock.now() : null;
				const sec = nowMs != null ? Math.floor(nowMs / 1000) : null;
				if (sec !== lastSecRef.current) {
					lastSecRef.current = sec;
					setRenderTick((t) => (t + 1) % 1000000);
				}
			}, 250);
		}
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [shouldTick, runningClockEnabled, isRunning, gameClock]);

	const toggleClock = useCallback(async () => {
		if (!gameId) return;
		if (isRunning) {
			await pauseGameClock();
		} else {
			await resumeGameClock();
		}
	}, [gameId, isRunning, pauseGameClock, resumeGameClock]);

	const getCurrentTime = useCallback(() => {
		return gameClock && typeof gameClock.now === "function"
			? gameClock.now()
			: null;
	}, [gameClock]);

	const displayTime =
		gameClock && typeof gameClock.now === "function" ? gameClock.now() : null;

	return {
		displayTime,
		isRunning,
		toggleClock,
		getCurrentTime,
		runningClockEnabled
	};
};

export default useRunningClock;
